import { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, X, MapPin, ChevronRight, Loader2, Clock } from 'lucide-react';
import { api } from '../../lib/api.js';
import { useMapStore } from '../../store/mapStore.js';

const RECENTS_KEY = 'wingman_recent_searches';
const MAX_RECENTS = 5;

interface Suggestion {
  id: string;
  name: string;
  lat: number;
  lng: number;
  subtitle?: string;
  isGeocode?: boolean;
  isRecent?: boolean;
}

function loadRecents(): Suggestion[] {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveRecent(s: Suggestion) {
  const recents = loadRecents().filter((r) => r.id !== s.id);
  recents.unshift({ ...s, isRecent: true });
  localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, MAX_RECENTS)));
}

export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();
  const setCenter = useMapStore((s) => s.setCenter);
  const setZoom = useMapStore((s) => s.setZoom);

  // Show recent searches when focused with no query
  const handleFocus = useCallback(() => {
    setFocused(true);
    if (!query) {
      const recents = loadRecents();
      if (recents.length > 0) {
        setSuggestions(recents);
        setShowSuggestions(true);
      }
    } else if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [query, suggestions.length]);

  useEffect(() => {
    if (query.length < 2) {
      if (!focused) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [placesResult, geocodeResult] = await Promise.all([
          api.search.autocomplete(query),
          api.search.geocode(query).catch(() => ({ data: [] as unknown[] })),
        ]);

        const placeSuggestions: Suggestion[] = placesResult.data.map((p) => ({
          id: p.id,
          name: p.name,
          lat: p.lat,
          lng: p.lng,
        }));

        const geocodeSuggestions: Suggestion[] = (geocodeResult.data as Array<{
          lat: number; lon: number; display_name: string; place_id: string;
        }>).slice(0, 3).map((g) => ({
          id: `geo_${g.place_id}`,
          name: g.display_name.split(',')[0],
          lat: g.lat,
          lng: g.lon,
          subtitle: g.display_name.split(',').slice(1, 3).join(',').trim(),
          isGeocode: true,
        }));

        setSuggestions([...placeSuggestions, ...geocodeSuggestions]);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, focused]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleSuggestionClick = (s: Suggestion) => {
    setShowSuggestions(false);
    setQuery(s.name);
    saveRecent(s);
    if (s.isGeocode || s.isRecent) {
      setCenter(s.lat, s.lng);
      setZoom(16);
    } else {
      navigate(`/place/${s.id}`);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setFocused(false);
      setShowSuggestions(false);
    }, 180);
  };

  const clearQuery = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const showRecents = focused && !query && suggestions.some((s) => s.isRecent);

  return (
    <div className={`search-bar`} style={{ position: 'absolute' }}>
      <Search size={15} style={{ flexShrink: 0, color: focused ? 'var(--accent)' : undefined }} />

      <form onSubmit={handleSubmit} style={{ flex: 1, minWidth: 0 }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Search target location or designation…"
          className="w-full outline-none"
          style={{ background: 'transparent', border: 'none' }}
          aria-label="Search for places and addresses"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="search-suggestions"
        />
      </form>

      {loading && (
        <Loader2 size={13} style={{ flexShrink: 0, color: 'var(--accent)', animation: 'spin 1s linear infinite' }} />
      )}

      {query && !loading && (
        <button type="button" onClick={clearQuery} style={{ color: 'var(--text-muted)', flexShrink: 0 }} aria-label="Clear search">
          <X size={14} />
        </button>
      )}

      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              overflow: 'hidden',
              zIndex: 20,
            }}
            id="search-suggestions"
            role="listbox"
          >
            {showRecents && (
              <div style={{
                padding: '6px 14px 4px',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--text-dim)',
                borderBottom: '1px solid var(--border)',
              }}>
                Recent
              </div>
            )}

            {suggestions.map((s) => (
              <button
                key={s.id}
                role="option"
                aria-selected={false}
                onClick={() => handleSuggestionClick(s)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid rgba(48,54,61,0.5)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background 100ms',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-dim)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {s.isRecent
                  ? <Clock size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                  : s.isGeocode
                  ? <MapPin size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                  : <ChevronRight size={13} style={{ flexShrink: 0, color: 'var(--accent)' }} />
                }
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {s.name}
                  </div>
                  {s.subtitle && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.subtitle}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
